import { useFormik } from 'formik';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import { InviteUserSchema } from './validationSchema';
import { pagesText } from '../../../../../constants/pagesText';
import Button from '../../../../../components/Button';
import useDialog from '../../../../../hooks/useDialog';
import useQueryData from '../../../../../queries/useQueryData';
import { COMPANY_MEMBERS_QUERY_KEY, CUSTOMER_QUERY_KEY } from '../../../../../constants/query';
import { customerIdSelector } from '../../../../../selectors/customer';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import CustomerService from 'src/services/customer.service';

const StyledTextField = styled(TextField, {
  shouldForwardProp: prop => prop !== 'isReadyOnly',
})(({ isReadyOnly }) => ({
  width: '100%',
  '& label.Mui-focused': {
    color: '#fff',
  },
  '& .MuiInput-underline:after': {
    borderColor: '#D1D5DB',
  },
  '& .MuiInputBase-root': {
    '& fieldset': {
      borderStyle: isReadyOnly ? 'dotted' : 'solid',
      borderColor: '#D1D5DB',
    },
  },
  '& .MuiInputBase-root.Mui-focused': {
    '& fieldset': {
      borderStyle: isReadyOnly ? 'dotted' : 'solid',
      borderColor: '#D1D5DB',
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#fff',
    '&.Mui-error': {
      color: '#FF4842',
    },
  },
  '& .MuiInputLabel-root': {
    lineHeight: '20px',
  },
}));

const InviteUserDialog = () => {
  const {
    open: openInviteDialog,
    onOpen: onOpenInviteDialog,
    onClose: onCloseInviteDialog,
  } = useDialog();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: inviteAsync, isLoading } = useMutation(CustomerService.inviteCustomer);

  const customerId = useQueryData(CUSTOMER_QUERY_KEY, customerIdSelector);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: InviteUserSchema,
    onSubmit: async (values, { resetForm }) => {
      resetForm();
      await inviteAsync(
        {
          ...values,
          firstName: '',
          lastName: '',
          customerId,
        },
        {
          onError: error => {
            enqueueSnackbar(error, {
              variant: 'error',
              autoHideDuration: 3000,
              preventDuplicate: true,
            });
          },

          onSuccess: () => {
            enqueueSnackbar(pagesText.inviteUsers.addCustomerSnackbar, {
              variant: 'success',
              autoHideDuration: 3000,
            });
          },

          onSettled: success => {
            queryClient.invalidateQueries([COMPANY_MEMBERS_QUERY_KEY, customerId]);

            if (!!success) {
              onCloseInviteDialog();
            }
          },
        },
      );
    },
  });

  return (
    <>
      <Button disabled={!customerId} variant="outlined" onClick={onOpenInviteDialog}>
        {pagesText.inviteUsers.invitesButton}
      </Button>
      <Dialog
        open={openInviteDialog}
        onClose={onCloseInviteDialog}
        PaperProps={{
          sx: {
            maxWidth: '400px',
            width: '100%',
          },
        }}
        TransitionProps={{
          onExit: () => {
            formik.resetForm();
          },
        }}>
        <DialogTitle>Invite User</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <StyledTextField
              label={pagesText.inviteUsers.inputFields.email}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              {...formik.getFieldProps('email')}
              size="small"
              InputProps={{
                sx: {
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: 'unset',
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#fff',
                  lineHeight: '14px',
                },
              }}
              {...formik.getFieldProps('email')}
            />
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ minHeight: '37px' }}
              variant="text"
              onClick={onCloseInviteDialog}
              styles={{
                color: '#B5B5B5',
                textTransform: 'capitalize',
              }}>
              Cancel
            </Button>
            <Button
              sx={{ minHeight: '37px' }}
              loading={isLoading}
              variant="contained"
              type="submit"
              disabled={!formik.dirty || isLoading}>
              Invite
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default InviteUserDialog;
